import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { api, setCsrfToken } from './api'
import type { User } from './types'
import { LoginPage } from './pages/LoginPage'
import { Layout } from './pages/Layout'
import { DashboardPage } from './pages/DashboardPage'
import { OrdersPage } from './pages/OrdersPage'
import { BatchesPage } from './pages/BatchesPage'
import { BatchDetailPage } from './pages/BatchDetailPage'
import { TeamPage } from './pages/TeamPage'
import { SettingsPage } from './pages/SettingsPage'
import { CampaignBrand } from './CampaignBrand'

const DriverRoutePage = lazy(() => import('./pages/DriverRoutePage').then((module) => ({ default: module.DriverRoutePage })))
const LiveTrackingPage = lazy(() => import('./pages/LiveTrackingPage').then((module) => ({ default: module.LiveTrackingPage })))
const SupervisorRoutePage = lazy(() => import('./pages/SupervisorRoutePage').then((module) => ({ default: module.SupervisorRoutePage })))

type SessionState = { loading: boolean; user: User | null }

export function App() {
  const [session, setSession] = useState<SessionState>({ loading: true, user: null })

  const refresh = async () => {
    try {
      const result = await api<{ user: User; csrfToken: string }>('/api/auth/me')
      setCsrfToken(result.csrfToken)
      setSession({ loading: false, user: result.user })
    } catch {
      setSession({ loading: false, user: null })
    }
  }

  useEffect(() => { void refresh() }, [])

  if (session.loading) return <div className="app-loading"><CampaignBrand variant="loading" inverse /><p>Carregando entregas...</p></div>
  if (!session.user) return <LoginPage onSuccess={refresh} />

  const isDriver = session.user.role === 'DRIVER'
  return <Suspense fallback={<div className="page-skeleton">Carregando interface…</div>}>
    <Routes>
      <Route element={<Layout user={session.user} onLogout={() => setSession({ loading: false, user: null })} />}>
        <Route index element={<Navigate to={isDriver ? '/minha-rota' : '/visao-geral'} replace />} />
        <Route path="visao-geral" element={isDriver ? <Navigate to="/minha-rota" replace /> : <DashboardPage />} />
        <Route path="pedidos" element={isDriver ? <Navigate to="/minha-rota" replace /> : <OrdersPage />} />
        <Route path="remessas" element={isDriver ? <Navigate to="/minha-rota" replace /> : <BatchesPage />} />
        <Route path="remessas/:id" element={isDriver ? <Navigate to="/minha-rota" replace /> : <BatchDetailPage />} />
        <Route path="rotas/:id" element={isDriver ? <Navigate to="/minha-rota" replace /> : <SupervisorRoutePage />} />
        <Route path="rastreamento" element={isDriver ? <Navigate to="/minha-rota" replace /> : <LiveTrackingPage />} />
        <Route path="equipe" element={session.user.role === 'ADMIN' ? <TeamPage /> : <Navigate to="/visao-geral" replace />} />
        <Route path="configuracoes" element={isDriver ? <Navigate to="/minha-rota" replace /> : <SettingsPage />} />
        <Route path="minha-rota" element={<DriverRoutePage user={session.user} />} />
        <Route path="*" element={<Navigate to={isDriver ? '/minha-rota' : '/visao-geral'} replace />} />
      </Route>
    </Routes>
  </Suspense>
}
